import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrapper, FormGroup, TitleContainer } from '../wrapper.styles';
import { TextInput } from '../../components/input/TextInput/TextInput';
import { TextBlock } from '@components/text/TextBlock';
import { NavigationButton, PageOrigin } from '@components/Button/NavigationButton/NavigationButton';
import { DropDownInput } from '@components/input/DropDownInput/DropDownInput';
import { pages } from '../../defaultVerbiages';
import { AddressAutoComplete } from '@components/input/AddressAutoComplete/AddressAutoComplete';
import DataPicker from '@components/input/DatePicker/DatePicker';
import { CheckConsent } from '@components/input/CheckConsent/CheckConsent';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { FormGroupComponent } from '@components/input/FormGroup/FormGroup';
import { PhoneInput } from '@/components/input/PhoneInput/PhoneInput';
import { TypeOfText } from '@components/text/TextBlock';
import { ModalSubmit } from '@/components/Modals/ModalSubmit/ModalSubmit';
import { PropertyType } from '@/types';
import { ModalResume } from '@/components/Modals/ModalResume/ModalResume';
import { useDataChangeDetection } from '@/hooks/useDataChangeDetection';
import { DefaultModal } from '@/components/Modals/DefaultModal/DefaultModal';
import { selectIsFirstTimeGetStarted, selectIsOpenResume } from '@/reducers/currentPage';
// import { ConfirmationModal } from '@/components/Modals/ConfirmModal/ConfirmModal';
// import { updateLoanApplication } from '@/reducers/LoanApplication';

interface GetStartedProps {
  onAccess: (page: string) => void;
}

// type YesNoEmpty = '' | 'yes' | 'no';

// interface A6ModalConfig {
//   body: string;
//   acceptButton: string;
//   button: string;
// }

// interface A6State {
//   isActive: boolean;
//   infoMessage: string;
//   showExistingLoanModal: boolean;
//   existingLoanAnswer: YesNoEmpty;
//   showFollowUpModal: boolean;
//   followUpAnswer: YesNoEmpty;
//   showHardStopModal: boolean;
//   modalConfig: A6ModalConfig;
// }

// type A6Config = {
//   isEnabled?: boolean;
//   followUpEnabled?: boolean;
//   config?: {
//     existingLoanQuestion?: string;
//     existingLoanHardStopMessage?: string;
//     twelveMonthHardStopMessage?: string;
//     followUpQuestion?: string;
//   };
// };

const GetStarted: React.FC<GetStartedProps> = ({ onAccess }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  // const a6Config = useMemo(() => {
  //   return (templateConfig?.features?.a6Validation ?? {}) as A6Config;
  // }, [templateConfig]);

  // const existingLoanQuestion = a6Config?.config?.existingLoanQuestion;
  // const hardStopMessageExistingLoan = a6Config?.config?.existingLoanHardStopMessage;
  // const hardStopMessage12Month = a6Config?.config?.twelveMonthHardStopMessage;

  const { openSoftPull, isResume } = useAppSelector((state) => state.currentPage);
  const isNewApplication = useAppSelector((state) => state.currentPage.isNewApplication);
  const isFirstTimeGetStarted = useAppSelector(selectIsFirstTimeGetStarted);
  const {
    firstName,
    eConsent,
    middleName,
    lastName,
    suffix,
    email,
    purposeOfLoan,
    suit,
    city,
    primaryUse,
    state,
    county,
    zip,
    dataOccupancyDate,
    propertyType,
    mailingCity,
    mailingState,
    mailingZip,
    address,
    mobile,
    home,
    work,
    mailingAddress,
  } = useAppSelector((state) => state.CurrentForm);
  const currentForm = useAppSelector((state) => state.CurrentForm);

  const [isMailingAddressSame, setIsMailingAddressSame] = useState(true);
  const [selectedPrimaryUse, setSelectedPrimaryUse] = React.useState('');
  const [selectedPropertyType, setSelectedPropertyType] = React.useState('');
  const [formError, setFormError] = React.useState(false);

  const getInitialPhoneData = () => {
    // check currentForm first (for existing data)
    if (currentForm.phone && currentForm.selectedPhoneType) {
      return {
        phone: currentForm.phone,
        selectedPhoneType: currentForm.selectedPhoneType,
        mobilePhone: currentForm.mobilePhone || mobile || '',
        homePhone: currentForm.homePhone || home || '',
        workPhone: currentForm.workPhone || work || '',
      };
    }

    // fallback to loanApplication borrower data
    const borrower = loanApplication?.borrowers?.[0];
    if (borrower?.borrowerPrimaryPhone) {
      return {
        phone: borrower.borrowerPrimaryPhone,
        selectedPhoneType: 'Mobile Phone', //display name
        mobilePhone: borrower.borrowerPrimaryPhone,
        homePhone: home || '',
        workPhone: work || '',
      };
    }
    if (borrower?.borrowerWorkPhone) {
      return {
        phone: borrower.borrowerWorkPhone,
        selectedPhoneType: 'Work Phone', //display name
        mobilePhone: mobile || '',
        homePhone: home || '',
        workPhone: borrower.borrowerWorkPhone,
      };
    }
    if (borrower?.borrowerAlternatePhone) {
      return {
        phone: borrower.borrowerAlternatePhone,
        selectedPhoneType: 'Home Phone', //display name
        mobilePhone: mobile || '',
        homePhone: borrower.borrowerAlternatePhone,
        workPhone: work || '',
      };
    }

    return {
      phone: '',
      selectedPhoneType: 'mobilePhone',
      mobilePhone: mobile || '',
      homePhone: home || '',
      workPhone: work || '',
    };
  };

  const { propertyStreetAddress, propertyStreetAddress2 } = loanApplication?.borrowerProperty || {};
  const initialPhoneData = getInitialPhoneData();

  const [formValues, setFormValues] = useState({
    firstName: firstName || '',
    middleName: middleName || '',
    lastName: lastName || '',
    suffix: suffix || '',
    email: email || '',
    purposeOfLoan: purposeOfLoan || '',
    address: propertyStreetAddress || address || '',
    suit: propertyStreetAddress2 || '',
    city: city || '',
    state: state || '',
    county: county || '',
    zip: zip || '',
    mailingCity: mailingCity || '',
    mailingState: mailingState || '',
    mailingZip: mailingZip || '',
    mailingSuit: '',
    primaryUse: primaryUse || '',
    propertyType: propertyType || '',
    dataOccupancyDate: dataOccupancyDate || '',
    mobile: mobile || '',
    home: home || '',
    work: work || '',
    phone: initialPhoneData.phone,
    mobilePhone: initialPhoneData.mobilePhone,
    homePhone: initialPhoneData.homePhone,
    workPhone: initialPhoneData.workPhone,
    selectedPhoneType: initialPhoneData.selectedPhoneType as 'mobilePhone' | 'workPhone' | 'homePhone',
  });
  const [isErrorForm, setIsErrorForm] = useState(false);
  const [typeOfPhone, setTypeOfPhone] = useState<'mobilePhone' | 'workPhone' | 'homePhone' | 'select'>('mobilePhone');

  useEffect(() => {
    if (currentForm.email && currentForm.email !== formValues.email) {
      setFormValues((prev) => ({
        ...prev,
        email: currentForm.email,
      }));
    }
  }, [currentForm.email, formValues.email]);

  useEffect(() => {
    if (typeOfPhone !== 'select') {
      dispatch({
        type: 'CurrentForm/addField',
        payload: { selectedPhoneType: typeOfPhone },
      });
    }
  }, [typeOfPhone, dispatch]);
  const prevTypeOfPhone = useRef<typeof typeOfPhone>();
  const isFirstRender = useRef(true);
  const [phoneFormError, setPhoneFormError] = useState(false);
  const isOpenResume = useAppSelector(selectIsOpenResume);
  // Change detection for ALL fields
  const { hasDataChanged, resetChangeDetection } = useDataChangeDetection([
    'firstName',
    'lastName',
    'middleName',
    'suffix',
    'email',
    'purposeOfLoan',
    'address',
    'suit',
    'city',
    'state',
    'county',
    'zip',
    'primaryUse',
    'propertyType',
    'dataOccupancyDate',
    'mobile',
    'home',
    'work',
    'phone',
    'mobilePhone',
    'homePhone',
    'workPhone',
    'selectedPhoneType',
    'eConsent',
    'softPullConsent',
  ]);

  // change detection for SENSITIVE fields only (firstName, lastName - DOB is tracked separately)
  const { hasDataChanged: hasSensitiveFieldsChanged } = useDataChangeDetection(['firstName', 'lastName']);

  // track DOB changes from LoanApplication state
  const [originalDOB, setOriginalDOB] = useState<string>(loanApplication?.borrowers[0]?.borrowerDOB || '');
  useEffect(() => {
    // initialize original DOB on component mount if not set
    if (!originalDOB && loanApplication?.borrowers[0]?.borrowerDOB) {
      setOriginalDOB(loanApplication.borrowers[0].borrowerDOB);
    }
  }, [loanApplication?.borrowers[0]?.borrowerDOB, originalDOB]);

  // Store original DOB in Redux when component mounts for ModalSubmit to access
  useEffect(() => {
    if (isFirstTimeGetStarted && loanApplication?.borrowers[0]?.borrowerDOB) {
      dispatch({
        type: 'CurrentForm/setOriginalValues',
        payload: {
          originalDOB: loanApplication.borrowers[0].borrowerDOB,
        },
      });
    }
  }, [isFirstTimeGetStarted, dispatch, loanApplication?.borrowers[0]?.borrowerDOB]);

  // combined function to check if sensitive fields (including DOB) have changed
  const hasSensitiveDataChanged = () => {
    const formFieldsChanged = hasSensitiveFieldsChanged();
    const currentDOB = loanApplication?.borrowers[0]?.borrowerDOB || '';
    // Only consider DOB changed if there was an original value and it's different
    const dobChanged = originalDOB && originalDOB !== currentDOB;
    return formFieldsChanged || dobChanged;
  };

  // soft credit pull warning modal state
  const [showSoftCreditInfoModal, setShowSoftCreditInfoModal] = useState(false);

  // track if we've already shown the warning modal for current session
  const [hasShownWarningForCurrentSession, setHasShownWarningForCurrentSession] = useState(false);

  // check if soft credit pull is enabled in lender's configuration
  const isSoftCreditPullEnabled = () => {
    return templateConfig?.features?.softCredit?.isEnabled ?? false;
  };

  // show warning modal about soft credit pull when SENSITIVE fields change AND user has visited before
  const checkSoftCreditImpact = () => {
    if (isSoftCreditPullEnabled() && hasSensitiveDataChanged() && !isFirstTimeGetStarted && !hasShownWarningForCurrentSession) {
      setShowSoftCreditInfoModal(true);
      setHasShownWarningForCurrentSession(true);
      // Return true to indicate modal was shown
      return true;
    }
    // Return false to indicate no modal was shown, can proceed
    return false;
  };

  // Track previous sensitive field state to detect new changes
  const [prevSensitiveFieldsState, setPrevSensitiveFieldsState] = useState<string>('');

  // Show modal immediately when SENSITIVE fields change on return visits (second time onwards)
  useEffect(() => {
    const softCreditEnabled = isSoftCreditPullEnabled();
    const sensitiveFieldsChanged = hasSensitiveDataChanged();

    // Create a state snapshot to detect actual new changes
    const currentSensitiveState = `${firstName}-${lastName}-${loanApplication?.borrowers[0]?.borrowerDOB || ''}`;

    // Only show warning modal if:
    // 1. Soft credit is enabled
    // 2. Not first time
    // 3. Sensitive fields have changed
    // 4. The sensitive field state is different from previous (new change detected)
    // 5. Haven't shown warning for current changes yet
    if (
      softCreditEnabled &&
      !isFirstTimeGetStarted &&
      !openSoftPull &&
      sensitiveFieldsChanged &&
      currentSensitiveState !== prevSensitiveFieldsState &&
      !hasShownWarningForCurrentSession
    ) {
      setShowSoftCreditInfoModal(true);
      setHasShownWarningForCurrentSession(true);
      setPrevSensitiveFieldsState(currentSensitiveState);
    } else if (!sensitiveFieldsChanged) {
      // Reset tracking when no changes detected
      setHasShownWarningForCurrentSession(false);
      setPrevSensitiveFieldsState(currentSensitiveState);
    }
  }, [
    isFirstTimeGetStarted,
    openSoftPull,
    firstName,
    lastName,
    loanApplication?.borrowers[0]?.borrowerDOB,
    hasShownWarningForCurrentSession,
    prevSensitiveFieldsState,
    templateConfig?.features?.softCredit?.isEnabled,
  ]);

  const countyConfig = useMemo(() => {
    const filter = templateConfig?.pages?.LetsGetStarted?.countyEligibilityFilter;
    return {
      isEnabled: filter?.isEnabled ?? false,
      isRequired: (filter?.isRequired && filter?.isEnabled) ?? false,
    };
  }, [templateConfig]);

  // const [a6State, setA6State] = useState<A6State>({
  //   isActive: false,
  //   infoMessage: '',
  //   showExistingLoanModal: false,
  //   existingLoanAnswer: '',
  //   showFollowUpModal: false,
  //   followUpAnswer: '',
  //   showHardStopModal: false,
  //   modalConfig: {
  //     body: '',
  //     acceptButton: '',
  //     button: '',
  //   },
  // });

  // const saveA6Response = (fieldKey: string, value: string) => {
  //   const timestamp = new Date().toISOString();

  //   dispatch(
  //     updateLoanApplication({
  //       [fieldKey]: value,
  //       [`${fieldKey}_timestamp`]: timestamp,
  //     }),
  //   );
  // };

  useEffect(() => {
    if (templateConfig && Object.keys(templateConfig).length && templateConfig?.lenderId) {
      const vmoRedesign = templateConfig.features?.vmoRedesign.isEnabled;
      const payload = {
        lenderId: templateConfig?.lenderId,
        configurationId: templateConfig?.configurationId,
        ...(vmoRedesign && { features: { vmoRedesign: true } }),
        propertyIsMailingAddress: true,
      };
      dispatch({
        type: 'LoanApplication/updateLoanApplication',
        payload: payload,
      });
    }
  }, [templateConfig, dispatch]);

  useEffect(() => {
    prevTypeOfPhone.current = typeOfPhone;
  }, [typeOfPhone]);

  useEffect(() => {
    const requiredValues = {
      firstName: formValues.firstName || '',
      lastName: formValues.lastName || '',
      email: formValues.email || '',
      purposeOfLoan: formValues.purposeOfLoan || '',
      PrimaryUse: selectedPrimaryUse,
      PropertyType: selectedPropertyType,
      address: formValues.address || '',
      city: formValues.city || '',
      state: formValues.state || '',
      zip: formValues.zip || '',
      dataOccupancyDate: formValues.dataOccupancyDate || '',
      ...(countyConfig.isEnabled && countyConfig.isRequired && { county: formValues.county }),
    };

    const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(requiredValues.email);
    const isValidProperty = !!selectedPrimaryUse && !!selectedPropertyType;
    const allFieldsFilled = Object.values(requiredValues).every((value) => {
      if (typeof value === 'boolean') return value;
      return typeof value === 'string' && value.trim() !== '';
    });

    setFormError(allFieldsFilled && isEmailValid && isValidProperty && !phoneFormError);
  }, [formValues, selectedPrimaryUse, selectedPropertyType, phoneFormError, countyConfig.isEnabled, countyConfig.isRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (value: string | number | boolean, name: string) => {
    setFormValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        [name]: value,
      };

      if (name === 'phone' && typeOfPhone && typeOfPhone !== 'select') {
        updatedValues[typeOfPhone] = value as string;
        updatedValues['selectedPhoneType'] = typeOfPhone;
      }

      const phoneTypeKeys = ['mobilePhone', 'homePhone', 'workPhone'] as const;
      if (phoneTypeKeys.includes(name as (typeof phoneTypeKeys)[number])) {
        const previousType = prevTypeOfPhone.current;
        if (previousType && previousType !== 'select' && previousType !== name) {
          updatedValues[previousType] = '' as any;
        }
        updatedValues['selectedPhoneType'] = name as any;
        updatedValues['phone'] = value as string;
        setTypeOfPhone(name as 'mobilePhone' | 'workPhone' | 'homePhone');
      }

      let myPayload: Record<string, any> = {};
      const hasOnlyEmail = currentForm.email && Object.keys(currentForm).length === 1;
      if (isFirstRender.current && hasOnlyEmail) {
        isFirstRender.current = false;
        myPayload = {
          ...updatedValues,
          email: currentForm.email,
        };
      } else {
        myPayload = { ...updatedValues };
        if (name !== 'email' && currentForm.email && !updatedValues.email) {
          myPayload.email = currentForm.email;
        }
      }

      dispatch({ type: 'CurrentForm/addField', payload: myPayload });

      return updatedValues;
    });
  };

  const handleDropdownChange = (value: string, propertyType?: string) => {
    if (propertyType === PropertyType.PrimaryUse) {
      setSelectedPrimaryUse(value);
    } else if (propertyType === PropertyType.PropertyType) {
      setSelectedPropertyType(value);
    }
    handleChange(value, propertyType || '');
  };

  useEffect(() => {
    const { propertyCity, propertyState, propertyStreetAddress, propertyZip, propertyStreetAddress2 } = loanApplication?.borrowerProperty || {};

    if (propertyStreetAddress) {
      setFormValues((prevValues) => {
        const updatedFormValues = {
          city: propertyCity || prevValues.city,
          state: propertyState || prevValues.state,
          address: propertyStreetAddress,
          zip: propertyZip || prevValues.zip,
          suit: propertyStreetAddress2 || prevValues.suit,
        };

        return {
          ...prevValues,
          ...updatedFormValues,
        };
      });
    }
  }, [loanApplication]);

  const handleMailingAddressCheckbox = (checked: boolean, type: string) => {
    if (type !== 'mailing') {
      handleChange(checked, type);
    } else {
      setIsMailingAddressSame(checked);

      if (checked) {
        const { propertyStreetAddress, propertyStreetAddress2, propertyCity, propertyState, propertyZip, propertyCounty, propertyLatitude, propertyLongitude } =
          loanApplication?.borrowerProperty || {};

        dispatch({
          type: 'LoanApplication/updateValue',
          payload: {
            ...loanApplication,
            propertyIsMailingAddress: true,
            mailingAddress: {
              mailingStreetAddress: propertyStreetAddress || '',
              mailingStreetAddress2: propertyStreetAddress2 || '',
              mailingCity: propertyCity || '',
              mailingState: propertyState || '',
              mailingCountry: 'United States',
              mailingCounty: propertyCounty || '',
              mailingZip: propertyZip || '',
              mailingLatitude: propertyLatitude || 0,
              mailingLongitude: propertyLongitude || 0,
            },
          },
        });
      } else {
        dispatch({
          type: 'LoanApplication/updateValue',
          payload: {
            ...loanApplication,
            propertyIsMailingAddress: false,
            mailingAddress: {
              mailingStreetAddress: '',
              mailingStreetAddress2: '',
              mailingCity: '',
              mailingState: '',
              mailingCountry: '',
              mailingCounty: '',
              mailingZip: '',
              mailingLatitude: 0,
              mailingLongitude: 0,
            },
          },
        });
      }
    }
  };

  const isNavigationButtonDisabled = !(formError && eConsent && !isErrorForm);

  // Sync county field from CurrentForm to formValues when it changes
  useEffect(() => {
    if (county !== formValues.county) {
      setFormValues((prev) => ({
        ...prev,
        county: county || '',
      }));
    }
  }, [county, formValues.county]);

  useEffect(() => {
    setFormValues((prevValues) => {
      const shouldUpdate = !prevValues.firstName || isResume;

      if (!shouldUpdate) {
        return prevValues;
      }

      return {
        ...prevValues,
        firstName: prevValues.firstName || firstName || '',
        middleName: prevValues.middleName || middleName || '',
        lastName: prevValues.lastName || lastName || '',
        suffix: prevValues.suffix || suffix || '',
        email: prevValues.email || email || '',
        purposeOfLoan: prevValues.purposeOfLoan || purposeOfLoan || '',
        address: prevValues.address || propertyStreetAddress || address || '',
        suit: prevValues.suit || suit || '',
        city: prevValues.city || city || '',
        state: prevValues.state || state || '',
        county: prevValues.county || county || '',
        zip: prevValues.zip || zip || '',
        mailingSuit: prevValues.mailingSuit || propertyStreetAddress2 || '',
        mailingCity: prevValues.mailingCity || mailingCity || '',
        mailingState: prevValues.mailingState || mailingState || '',
        mailingZip: prevValues.mailingZip || mailingZip || '',
        primaryUse: prevValues.primaryUse || primaryUse || '',
        propertyType: prevValues.propertyType || propertyType || '',
        dataOccupancyDate: prevValues.dataOccupancyDate || dataOccupancyDate || '',
        mobile: prevValues.mobile || mobile || '',
        home: prevValues.home || home || '',
        work: prevValues.work || work || '',
        phone: prevValues.phone || '',
        mobilePhone: prevValues.mobilePhone || mobile || '',
        homePhone: prevValues.homePhone || home || '',
        workPhone: prevValues.workPhone || work || '',
        selectedPhoneType: prevValues.selectedPhoneType || 'mobilePhone',
      };
    });

    setSelectedPrimaryUse(primaryUse || '');
    setSelectedPropertyType(propertyType || '');
  }, [isResume, primaryUse, propertyType]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBorrowerPhone = () => {
    if (currentForm.phone) {
      switch (currentForm.phone) {
        case currentForm.mobilePhone:
          return { phone: currentForm.mobilePhone, type: 'Mobile Phone' };
        case currentForm.homePhone:
          return { phone: currentForm.homePhone, type: 'Home Phone' };
        case currentForm.workPhone:
          return { phone: currentForm.workPhone, type: 'Work Phone' };
      }
    }

    const borrower = loanApplication?.borrowers?.[0];
    if (borrower) {
      if (borrower.borrowerPrimaryPhone) {
        // return { phone: borrower.borrowerPrimaryPhone, type: 'mobilePhone' };
        return { phone: borrower.borrowerPrimaryPhone, type: 'Mobile Phone' };
      }
      if (borrower.borrowerWorkPhone) {
        // return { phone: borrower.borrowerWorkPhone, type: 'workPhone' };
        return { phone: borrower.borrowerWorkPhone, type: 'Work Phone' };
      }
      if (borrower.borrowerAlternatePhone) {
        // return { phone: borrower.borrowerAlternatePhone, type: 'homePhone' };
        return { phone: borrower.borrowerAlternatePhone, type: 'Home Phone' };
      }
    }

    return { phone: '', type: '' };
  };

  const { phone, type } = getBorrowerPhone();

  // useEffect(() => {
  //   const isTX = formValues.state === 'TX';
  //   const hasPrimaryUse = !!formValues.primaryUse;

  //   setA6State((prev) => ({
  //     ...prev,
  //     isActive: isTX,
  //     infoMessage: '',
  //     existingLoanAnswer: '',
  //     followUpAnswer: '',
  //   }));

  //   if (!isTX) {
  //     setFormError(true);
  //     return;
  //   }

  //   if (!hasPrimaryUse) {
  //     return;
  //   }

  //   const isNotPrimary = formValues.primaryUse !== 'Primary Residence';

  //   if (isNotPrimary) {
  //     setA6State((prev) => ({
  //       ...prev,
  //       infoMessage: 'Texas A6 loans are only allowed on primary residences.',
  //     }));
  //     setFormError(false);
  //     return;
  //   }

  //   setA6State((prev) => ({ ...prev, infoMessage: '' }));
  //   setFormError(true);

  //   if (!loanApplication?.a6_existing_loan) {
  //     setA6State((prev) => ({
  //       ...prev,
  //       modalConfig: {
  //         body: existingLoanQuestion || 'Do you currently have a home equity loan, cash-out refinance, or HELOC on this property?',
  //         acceptButton: 'Yes',
  //         button: 'No',
  //       },
  //       showExistingLoanModal: true,
  //     }));
  //   }
  // }, [formValues.state, formValues.primaryUse, loanApplication?.a6_existing_loan, existingLoanQuestion]);

  // const handleExistingLoanConfirmation = (confirmed: boolean) => {
  //   setA6State((prev) => ({
  //     ...prev,
  //     showExistingLoanModal: false,
  //     existingLoanAnswer: confirmed ? 'yes' : 'no',
  //   }));
  //   saveA6Response('a6_existing_loan', confirmed ? 'yes' : 'no');

  //   if (confirmed) {
  //     if (a6Config?.followUpEnabled) {
  //       setA6State((prev) => ({ ...prev, showFollowUpModal: true }));
  //     } else {
  //       setA6State((prev) => ({
  //         ...prev,
  //         modalConfig: {
  //           body: hardStopMessageExistingLoan || 'Texas A6 loans require the existing equity loan to be closed before a new one can be made.',
  //           acceptButton: 'OK',
  //           button: 'Close',
  //         },
  //         showHardStopModal: true,
  //       }));
  //     }
  //   }
  // };

  // const handleFollowUpModal = (confirmed: boolean) => {
  //   setA6State((prev) => ({
  //     ...prev,
  //     showFollowUpModal: false,
  //     followUpAnswer: confirmed ? 'yes' : 'no',
  //   }));
  //   saveA6Response('a6_follow_up', confirmed ? 'yes' : 'no');

  //   if (confirmed) {
  //     setA6State((prev) => ({
  //       ...prev,
  //       modalConfig: {
  //         body: hardStopMessage12Month || 'Texas A6 loans require a 12-month waiting period before a new equity loan can be made.',
  //         acceptButton: 'OK',
  //         button: 'Close',
  //       },
  //       showHardStopModal: true,
  //     }));
  //   }
  // };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <TitleContainer data-testid="title-container">
        <TextBlock
          text={templateConfig?.pages?.LetsGetStarted?.pageContainer?.title?.title || pages.LetsGetStarted.pageContainer.title.title}
          direction={'center'}
          color="textPrimary"
          type={TypeOfText.H1}
          isHidden={templateConfig?.pages?.LetsGetStarted?.pageContainer?.title?.isHidden ?? false}
        />
        <TextBlock
          text={templateConfig?.pages?.LetsGetStarted?.pageContainer?.subtitle?.title || pages.LetsGetStarted.pageContainer.subtitle.title}
          direction={'center'}
          color="default"
          type={TypeOfText.Subtitle}
          isHidden={templateConfig?.pages?.LetsGetStarted?.pageContainer?.subtitle?.isHidden ?? false}
        />
      </TitleContainer>
      <Wrapper $gap="0.6rem">
        <TextBlock
          text={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.consumerInformation?.text || pages.LetsGetStarted.typeOfInformation.consumerInformation.text}
          direction={'flex-start'}
          color="default"
          type={TypeOfText.H2}
          isHidden={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.consumerInformation?.isHidden ?? false}
        />
        <FormGroupComponent onChange={handleChange}>
          <TextBlock
            text={templateConfig?.pages?.LetsGetStarted?.question?.basicInformation?.text || pages.LetsGetStarted.question.basicInformation.text}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H3}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.basicInformation?.isHidden ?? false}
          />
          <TextInput
            propertyType={PropertyType.FirstName}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.firstName?.text || pages.LetsGetStarted.consumerInformation.firstName.text}
            typeOfIcon="text"
            dataTestId="firstName"
            size={'32%'}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.firstName?.isHidden ?? false}
            value={firstName || ''}
          />
          <TextInput
            propertyType={PropertyType.MiddleName}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.middleName?.text || pages.LetsGetStarted.consumerInformation.middleName.text}
            typeOfIcon="text"
            size={'20%'}
            dataTestId="middleName"
            isRequired={false}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.middleName?.isHidden ?? false}
            value={middleName || ''}
          />
          <TextInput
            propertyType={PropertyType.LastName}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.lastName?.text || pages.LetsGetStarted.consumerInformation.lastName.text}
            typeOfIcon="text"
            size={'32%'}
            dataTestId="lastName"
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.lastName?.isHidden ?? false}
            value={lastName || ''}
          />
          <DropDownInput
            dataTestId="suffix"
            propertyType={PropertyType.Suffix}
            placeHolder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suffix?.title || pages.LetsGetStarted.consumerInformation.suffix.title}
            options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suffix?.options}
            isRequired={false}
            size={'10%'}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suffix?.isHidden ?? false}
            capitalizeWord={false}
          />
          <TextInput
            propertyType={PropertyType.Email}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.email?.text || pages.LetsGetStarted.consumerInformation.email.text}
            size={'50%'}
            dataTestId="email"
            typeOfIcon="email"
            type="email"
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.email?.isHidden ?? false}
            value={email || ''}
          />
          <PhoneInput
            propertyType={PropertyType.Phone}
            placeHolder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.titleInput || pages.LetsGetStarted.consumerInformation.phone.titleInput}
            dataTestId="phone"
            label={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.titleDropDown || pages.LetsGetStarted.consumerInformation.phone.titleDropDown}
            value={phone}
            selectedOption={type}
            size={'30%'}
            type="phone"
            options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.options}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.phone?.isHidden ?? false}
            setTypeOfPhone={setTypeOfPhone}
            setIsErrorForm={setPhoneFormError}
            isSentitiveInfo={true}
          />
          <DropDownInput
            propertyType={PropertyType.PurposeOfLoan}
            placeHolder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.purposeOfLoan?.text || pages.LetsGetStarted.consumerInformation.purposeOfLoan.text}
            dataTestId="purposeOfLoan"
            options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.purposeOfLoan?.options}
            value={loanApplication?.borrowers?.[0]?.borrowerLoanPurpose || ''}
            size={'30%'}
            typeOfIcon="dollar"
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.purposeOfLoan?.isHidden ?? false}
          />
        </FormGroupComponent>
        <TextBlock
          text={templateConfig?.pages?.LetsGetStarted?.protectInformation?.text || pages.LetsGetStarted.protectInformation.text}
          direction={'center'}
          color="textSecondary"
          type={TypeOfText.H4}
          isLinkModal={true}
          infoModal={templateConfig?.pages?.LetsGetStarted?.modals?.protectInformation || pages.LetsGetStarted.modals.protectInformation}
          isHidden={templateConfig?.pages?.LetsGetStarted?.protectInformation?.isHidden ?? false}
        />
      </Wrapper>
      <Wrapper $gap="0.6rem">
        <FormGroupComponent onChange={handleChange} data-testid="address-form">
          <TextBlock
            text={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.PropertyInformation?.text || pages.LetsGetStarted.typeOfInformation.PropertyInformation.text}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H1}
            isHidden={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.PropertyInformation?.isHidden ?? false}
          />
          <TextBlock
            text={templateConfig?.pages?.LetsGetStarted?.question?.addressProperty?.text || pages.LetsGetStarted.question.addressProperty.text}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H3}
            isInfoModal={!!templateConfig?.pages?.LetsGetStarted?.question?.addressProperty?.infoModal}
            infoModal={templateConfig?.pages?.LetsGetStarted?.question?.addressProperty?.infoModal || {}}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.addressProperty?.isHidden ?? false}
          />
          <AddressAutoComplete
            size="60%"
            onChange={handleChange}
            typeOfIcon="address"
            propertyType={PropertyType.Address}
            value={formValues.address || loanApplication?.borrowerProperty?.propertyStreetAddress || ''}
            isDisabled={templateConfig?.isStartingFromNewAddress}
          />
          <TextInput
            propertyType={PropertyType.Suit}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suit?.text || pages.LetsGetStarted.consumerInformation.suit.text}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suit?.isHidden ?? false}
            size="20%"
            dataTestId="suit"
            value={formValues.suit || loanApplication?.borrowerProperty?.propertyStreetAddress2 || ''}
            isRequired={false}
            isDisabled={templateConfig?.isStartingFromNewAddress}
          />
          <TextInput
            propertyType={PropertyType.City}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.city?.text || pages.LetsGetStarted.consumerInformation.city.text}
            value={formValues.city || loanApplication?.borrowerProperty?.propertyCity || ''}
            size="40%"
            dataTestId="city"
            setIsErrorForm={setIsErrorForm}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.city?.isHidden ?? false}
            isDisabled={templateConfig?.isStartingFromNewAddress}
          />
          <TextInput
            propertyType={PropertyType.County}
            value={formValues.county || loanApplication?.borrowerProperty?.propertyCounty}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.county?.text || 'County'}
            size="30%"
            dataTestId="county"
            setIsErrorForm={setIsErrorForm}
            isHidden={!countyConfig.isEnabled}
            isRequired={countyConfig.isRequired}
          />
          <DropDownInput
            propertyType={PropertyType.State}
            placeHolder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.text || pages.LetsGetStarted.consumerInformation.state.text}
            options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.options}
            value={formValues.state || loanApplication?.borrowerProperty?.propertyState || ''}
            size="5%"
            dataTestId="state"
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.isHidden ?? false}
            capitalizeWord={false}
            isDisabled={templateConfig?.isStartingFromNewAddress}
          />
          <TextInput
            propertyType={PropertyType.Zip}
            value={formValues.zip || loanApplication?.borrowerProperty?.propertyZip || ''}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.zip?.text || pages.LetsGetStarted.consumerInformation.zip.text}
            size="20%"
            dataTestId="zip"
            setIsErrorForm={setIsErrorForm}
            isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.zip?.isHidden ?? false}
            isDisabled={templateConfig?.isStartingFromNewAddress}
          />
        </FormGroupComponent>
        <CheckConsent
          type="mailing"
          text={templateConfig?.pages?.LetsGetStarted?.propertyMailingAddress?.checkText || pages.LetsGetStarted.propertyMailingAddress.checkText}
          direction="center"
          checked={true}
          onChange={handleMailingAddressCheckbox}
          isHidden={templateConfig?.pages?.LetsGetStarted?.propertyMailingAddress?.isHidden ?? false}
        />
        {!isMailingAddressSame && (
          <>
            <TextBlock
              text={templateConfig?.pages?.LetsGetStarted?.propertyMailingAddress?.information || pages.LetsGetStarted.propertyMailingAddress.information}
              direction={'flex-start'}
              color="default"
              type={TypeOfText.H3}
            />
            <FormGroupComponent onChange={handleChange} data-testid="address-form">
              <AddressAutoComplete
                size="60%"
                typeOfIcon="address"
                isMailingAddress={true}
                value={loanApplication?.mailingAddress?.mailingStreetAddress || mailingAddress || ''}
                propertyType={PropertyType.MailingAddress}
              />
              <TextInput
                propertyType={PropertyType.Suit}
                placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.suit?.text || pages.LetsGetStarted.consumerInformation.suit.text}
                size="20%"
                dataTestId="mailingSuit"
                value={loanApplication?.mailingAddress?.mailingStreetAddress2 || ''}
                isRequired={false}
              />
              <TextInput
                propertyType={PropertyType.MailingCity}
                placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.city?.text || pages?.LetsGetStarted?.consumerInformation?.city?.text}
                value={loanApplication?.mailingAddress?.mailingCity || ''}
                size="40%"
                dataTestId="mailingCity"
                isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.city?.isHidden ?? false}
              />
              <DropDownInput
                propertyType={PropertyType.MailingState}
                placeHolder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.text || pages?.LetsGetStarted?.consumerInformation?.state?.text}
                options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.options}
                value={loanApplication?.mailingAddress?.mailingState || ''}
                size="12%"
                dataTestId="mailingState"
                isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.state?.isHidden ?? false}
                capitalizeWord={false}
              />
              <TextInput
                propertyType={PropertyType.MailingZip}
                value={loanApplication?.mailingAddress?.mailingZip || ''}
                placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.zip?.text || pages?.LetsGetStarted?.consumerInformation?.zip?.text}
                size="20%"
                dataTestId="mailingZip"
                isHidden={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.zip?.isHidden ?? false}
              />
            </FormGroupComponent>
          </>
        )}
        <FormGroup>
          <TextBlock
            text={templateConfig?.pages?.LetsGetStarted?.question?.useOfProperty?.text || pages?.LetsGetStarted?.question?.useOfProperty?.text}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H3}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.useOfProperty?.isHidden ?? false}
          />
          <DropDownInput
            propertyType={PropertyType.PrimaryUse}
            placeHolder="Select Property Use"
            dataTestId="primaryUse"
            options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.propertyUse?.options}
            value={selectedPrimaryUse}
            size={'50%'}
            maxWidth={'50%'}
            onChange={handleDropdownChange}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.useOfProperty?.isHidden ?? false}
          />
          {/* {a6State.isActive && a6State.infoMessage && <TextBlock text={a6State.infoMessage} type={TypeOfText.Subtitle} color="default" direction="flex-start" />} */}
          {/* comment to deploy */}
          <TextBlock
            text={templateConfig?.pages?.LetsGetStarted?.question?.propertyType?.text || pages?.LetsGetStarted?.question?.propertyType?.text}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H3}
            isInfoModal={!!templateConfig?.pages?.LetsGetStarted?.question?.propertyType?.infoModal}
            infoModal={templateConfig?.pages?.LetsGetStarted?.question?.propertyType?.infoModal || {}}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.propertyType?.isHidden ?? false}
          />
        </FormGroup>
        <FormGroup>
          <DropDownInput
            propertyType={PropertyType.PropertyType}
            placeHolder="Select Property Type"
            dataTestId="propertyType"
            options={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.propertyType?.options}
            value={selectedPropertyType}
            size={'50%'}
            maxWidth={'50%'}
            onChange={handleDropdownChange}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.propertyType?.isHidden ?? false}
          />
        </FormGroup>
        <FormGroup>
          <TextBlock
            text={templateConfig?.pages?.LetsGetStarted?.question?.purchaseDate?.text || pages?.LetsGetStarted?.question?.purchaseDate?.text}
            direction={'flex-start'}
            color="default"
            type={TypeOfText.H3}
            isInfoModal={!!templateConfig?.pages?.LetsGetStarted?.question?.purchaseDate?.infoModal}
            infoModal={templateConfig?.pages?.LetsGetStarted?.question?.purchaseDate?.infoModal || {}}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.purchaseDate?.isHidden ?? false}
          />
        </FormGroup>
        <FormGroup>
          <DataPicker
            propertyType={PropertyType.DataOccupancyDate}
            onChange={handleChange}
            dataTestId="occupancyDate"
            propertyName={'propertyOccupancyDate'}
            placeholder={templateConfig?.pages?.LetsGetStarted?.consumerInformation?.purchaseDate?.text || pages?.LetsGetStarted?.consumerInformation?.purchaseDate?.text}
            typeOfIcon="date"
            setIsErrorForm={setIsErrorForm}
            isHidden={templateConfig?.pages?.LetsGetStarted?.question?.purchaseDate?.isHidden ?? false}
            value={dataOccupancyDate || formValues.dataOccupancyDate || ''}
          />
        </FormGroup>
      </Wrapper>
      <Wrapper $gap="1rem">
        <TextBlock
          text={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.consent?.text || pages?.LetsGetStarted?.typeOfInformation?.consent?.text}
          isHidden={templateConfig?.pages?.LetsGetStarted?.typeOfInformation?.consent?.isHidden ?? false}
          direction={'flex-start'}
          color="default"
          type={TypeOfText.H2}
        />
        <CheckConsent
          type={'softPullConsent'}
          text={templateConfig?.pages?.LetsGetStarted?.softPull?.text || pages?.LetsGetStarted?.softPull?.text}
          isInfoModal={!templateConfig?.pages?.LetsGetStarted?.softPull?.isHidden}
          infoModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoSoftPull || pages?.LetsGetStarted?.modals?.infoSoftPull}
          direction={'center'}
          onChange={handleMailingAddressCheckbox}
          isHidden={templateConfig?.pages?.LetsGetStarted?.softPull?.isHidden ?? false}
          isPointer
        />
        <CheckConsent
          type={'eConsent'}
          text={templateConfig?.pages?.LetsGetStarted?.eConsent?.text || pages?.LetsGetStarted?.eConsent?.text}
          direction={'center'}
          isInfoModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoEconsent?.isInfoModal || false}
          infoModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoEconsent || pages?.LetsGetStarted?.modals?.infoEconsent}
          onChange={handleMailingAddressCheckbox}
          isHidden={templateConfig?.pages?.LetsGetStarted?.eConsent?.isHidden ?? false}
          isPointer
        />
      </Wrapper>
      {/* <ConfirmationModal
        openModal={a6State.showExistingLoanModal}
        setOpenModal={(isOpen) => setA6State((prev) => ({ ...prev, showExistingLoanModal: isOpen }))}
        handleModalClose={handleExistingLoanConfirmation}
        infoModal={a6State.modalConfig}
      /> */}

      {/* <ConfirmationModal
        openModal={a6State.showFollowUpModal}
        setOpenModal={(isOpen) => setA6State((prev) => ({ ...prev, showFollowUpModal: isOpen }))}
        handleModalClose={handleFollowUpModal}
        infoModal={{
          body: a6Config?.config?.followUpQuestion || 'Was that loan originated within the past 12 months?',
          acceptButton: 'Yes',
          button: 'No',
        }}
      /> */}

      {/* <ConfirmationModal
        openModal={a6State.showHardStopModal}
        setOpenModal={(isOpen) => setA6State((prev) => ({ ...prev, showHardStopModal: isOpen }))}
        handleModalClose={() => setA6State((prev) => ({ ...prev, showHardStopModal: false }))}
        infoModal={a6State.modalConfig}
      /> */}

      <NavigationButton
        variant="primary"
        nextButton={templateConfig?.pages?.LetsGetStarted?.NavigationButton?.nextButton || 'Next'}
        backButton={templateConfig?.pages?.LetsGetStarted?.NavigationButton?.backButton?.text || 'Back'}
        isBackButton={templateConfig?.pages?.LetsGetStarted?.NavigationButton?.backButton?.isHidden ?? false}
        disabled={isNavigationButtonDisabled}
        onAccess={onAccess}
        navigation={navigate}
        currentPage={PageOrigin.GET_STARTED}
        type="submit"
        hasDataChanged={hasDataChanged}
        resetChangeDetection={resetChangeDetection}
        onNext={checkSoftCreditImpact}
      />
      {openSoftPull && (
        <ModalSubmit
          openModal={openSoftPull}
          setOpenModal={() => {}}
          infoSubmitModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoSubmitModal || pages?.LetsGetStarted?.modals?.infoSubmitModal}
          onAccess={onAccess}
          hasDataChanged={hasDataChanged}
          resetChangeDetection={resetChangeDetection}
        />
      )}
      {isOpenResume && !isNewApplication && <ModalResume />}
      {showSoftCreditInfoModal && (
        <DefaultModal
          openModal={showSoftCreditInfoModal}
          setOpenModal={(isOpen) => {
            if (!isOpen) {
              setShowSoftCreditInfoModal(false);
              // Don't reset session tracking - keep it true so modal doesn't re-appear
              // until new sensitive field changes are made
            }
          }}
          isErrorModal={false}
          infoModal={{
            header: (templateConfig?.pages?.LetsGetStarted?.modals as any)?.softCreditWarning?.header || 'Warning: Changes May Require a new Soft Credit Pull',
            body:
              (templateConfig?.pages?.LetsGetStarted?.modals as any)?.softCreditWarning?.body ||
              'Changes to your First Name, Last Name, or Date of Birth after already progressing in your application may require a new soft credit pull.',
            button: (templateConfig?.pages?.LetsGetStarted?.modals as any)?.softCreditWarning?.button || 'Ok',
          }}
        />
      )}
    </form>
  );
};

export default GetStarted;
